/**
 * Universal Builder — inject one line into the CodeBuild PTY via DynamoDB
 * slot (paired with ``stream_runner`` polling). Creator / workbench roles only.
 *
 * Uses ``POST /networks/{id}/builder/cli-input`` when ``VITE_STEPFLOW_UB_APPROVALS=1``,
 * else the legacy compact path ``POST …/cli-input``.
 */
import { getRestApiBearerToken } from './apiToken';
import { UB_APPROVALS_ENABLED } from '../constants/universalBuilderFlags';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.stepflow.dev';

const CLI_INPUT_SUFFIX = UB_APPROVALS_ENABLED ? '/builder/cli-input' : '/cli-input';

/**
 * @param {string} networkId
 * @param {{ runId: string, line: string, poolId?: string, waitEventId?: string }} body
 *   poolId is optional; when present, the backend broadcasts BUILDER_INPUT_RESOLVED
 *   to the same AppSync pool channel so peer subscribers dismiss the band in real time.
 *   waitEventId is the eventId of the BUILDER_INPUT_WAIT envelope being answered;
 *   forwarded in the RESOLVED payload so peers can match RESOLVED → WAIT by eventId
 *   (multi-turn sessions reuse the same runId across many WAITs).
 */
export async function postUniversalBuilderCliLine(networkId, body) {
  const token = getRestApiBearerToken();
  if (!token) throw new Error('Not authenticated');
  const res = await fetch(
    `${API_BASE_URL}/networks/${encodeURIComponent(networkId)}${CLI_INPUT_SUFFIX}`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(
      typeof data.message === 'string' && data.message.trim()
        ? data.message
        : data.error || `HTTP ${res.status}`,
    );
    err.httpStatus = res.status;
    err.errorCode = typeof data.error === 'string' ? data.error : '';
    throw err;
  }
  return data;
}

/**
 * STOP a running builder (always-available kill switch, both background + interactive).
 * Reuses the cli-input endpoint with ``{cancel:true}`` — the backend flags the
 * run's CLI_PENDING row (builder's cancel-watcher unwinds gracefully) and issues
 * a CodeBuild stop-build hard fallback.
 * @param {string} networkId
 * @param {{ runId: string, poolId?: string }} body
 */
export async function cancelUniversalBuilderRun(networkId, body) {
  return postUniversalBuilderCliLine(networkId, { ...body, cancel: true });
}
